import type { ThemeInput } from "@cos/shared";
import { contrastRatio, hexToOklch, oklchToHex, relativeLuminance, type Oklch } from "./oklch.js";

export interface ColorTokens {
  background: string;
  surface: string;
  surfaceRaised: string;
  border: string;
  borderSubtle: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  brand: string;
  brandSubtle: string;
  brandHover: string;
  brandActive: string;
  onBrand: string;
  focusRing: string;
  danger: string;
  onDanger: string;
}

export interface DerivedTheme {
  light: ColorTokens;
  dark: ColorTokens;
  /** Passed through rather than reinterpreted here — the widget's CSS decides what "minimal" means structurally (fewer borders/shadows); this package only owns color/spacing/radius math. */
  preset: ThemeInput["preset"];
  radius: string;
  radiusLauncher: string;
  spacingScale: number;
  fontFamily: string;
  fontUrl?: string;
}

const RADIUS_PX: Record<ThemeInput["radius"], string> = {
  sm: "6px",
  md: "10px",
  lg: "16px",
  full: "9999px",
};

const SPACING_SCALE: Record<ThemeInput["density"], number> = {
  comfortable: 1,
  compact: 0.8,
};

const SYSTEM_FONT_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

function resolveFont(font: ThemeInput["font"]): { fontFamily: string; fontUrl?: string } {
  if (font === "system") return { fontFamily: SYSTEM_FONT_STACK };
  if (font === "inherit") return { fontFamily: "inherit" };
  return { fontFamily: font.family, fontUrl: font.url };
}

function clampL(l: number): number {
  return Math.min(1, Math.max(0, l));
}

function withLightness(base: Oklch, l: number): Oklch {
  return { ...base, l: clampL(l) };
}

/**
 * The text color placed on a colored surface (brand button, danger badge):
 * whichever of near-black/white contrasts better, so a "hostile" brand
 * color (neon, near-white, near-black) still produces a readable label
 * instead of assuming dark-text-on-light-brand. See PLAN.md "Accessibility
 * is enforced, not hoped for."
 */
function bestOnColor(bgHex: string): string {
  const candidates = ["#0a0a0a", "#ffffff"];
  let best = candidates[0] as string;
  let bestRatio = 0;
  for (const candidate of candidates) {
    const ratio = contrastRatio(bgHex, candidate);
    if (ratio > bestRatio) {
      bestRatio = ratio;
      best = candidate;
    }
  }
  return best;
}

/**
 * A focus ring only needs the non-text UI-component contrast floor (WCAG
 * 2.4.11, 3:1), not the 4.5:1 text floor — so unlike `bestOnColor` it stays
 * the brand hue rather than collapsing to black/white, and only its
 * lightness is pushed until it clears 3:1 against the background.
 */
function focusRingFor(brandOklch: Oklch, backgroundHex: string): string {
  // Dark backgrounds need a *lighter* ring to separate from them; light backgrounds need a darker one.
  const step = relativeLuminance(backgroundHex) > 0.5 ? -0.06 : 0.06;
  let candidate = brandOklch;
  for (let i = 0; i < 20 && contrastRatio(oklchToHex(candidate), backgroundHex) < 3; i++) {
    candidate = withLightness(candidate, candidate.l + step);
  }
  return oklchToHex(candidate);
}

function buildPalette(mode: "light" | "dark", brandOklch: Oklch): ColorTokens {
  const isDark = mode === "dark";

  const background = isDark ? "#17181b" : "#ffffff";
  const surface = isDark ? "#1e1f23" : "#f7f7f8";
  const surfaceRaised = isDark ? "#202227" : "#ffffff";
  const border = isDark ? "#35363c" : "#e2e2e5";
  const borderSubtle = isDark ? "#2a2b30" : "#eeeeef";
  const textPrimary = isDark ? "#f5f5f6" : "#111214";
  const textSecondary = isDark ? "#c7c7cc" : "#45454c";
  const textMuted = isDark ? "#9a9aa1" : "#63636a";

  const brand = oklchToHex(brandOklch);
  const brandSubtle = oklchToHex(withLightness(brandOklch, isDark ? brandOklch.l * 0.45 : brandOklch.l + (1 - brandOklch.l) * 0.88));
  const brandHover = oklchToHex(withLightness(brandOklch, brandOklch.l * (isDark ? 1.12 : 0.92)));
  const brandActive = oklchToHex(withLightness(brandOklch, brandOklch.l * (isDark ? 1.22 : 0.84)));
  const onBrand = bestOnColor(brand);

  const danger = isDark ? "#f87171" : "#b42318";
  const onDanger = bestOnColor(danger);

  const focusRing = focusRingFor(brandOklch, background);

  return {
    background,
    surface,
    surfaceRaised,
    border,
    borderSubtle,
    textPrimary,
    textSecondary,
    textMuted,
    brand,
    brandSubtle,
    brandHover,
    brandActive,
    onBrand,
    focusRing,
    danger,
    onDanger,
  };
}

/**
 * The single derivation function shared by the panel's live preview, the
 * widget applying a published theme, and contrast validation at save time —
 * see PLAN.md "Theming". Both light and dark palettes are always computed;
 * `surface: 'auto'` is what tells the consumer to ship both (behind
 * `prefers-color-scheme`) rather than picking one here.
 */
export function deriveTheme(input: ThemeInput): DerivedTheme {
  const brandOklch = hexToOklch(input.brand);
  const font = resolveFont(input.font);

  return {
    light: buildPalette("light", brandOklch),
    dark: buildPalette("dark", brandOklch),
    preset: input.preset,
    radius: RADIUS_PX[input.radius],
    radiusLauncher: input.radius === "full" ? "9999px" : RADIUS_PX.lg,
    spacingScale: SPACING_SCALE[input.density],
    fontFamily: font.fontFamily,
    fontUrl: font.fontUrl,
  };
}
